import { ref, computed } from 'vue'
import { useRegisterSW } from 'virtual:pwa-register/vue'
import { useAppStore } from '../stores/appStore'

// Module-level singletons so update state is shared across all components
const isCheckingForUpdates = ref(false)
const isDownloadingUpdate = ref(false)
const lastCheckTimestamp = ref<number | null>(null)
const manualUpdateFound = ref(false)
const updateAvailableVersion = ref<string | null>(null)
const registeredSW = ref<ServiceWorkerRegistration | null>(null)
const isReloading = ref(false)

let rawNeedRefreshRef: any = null
let rawUpdateSWFn: ((reloadPage?: boolean) => Promise<void>) | null = null
let listenersInitialized = false

export function _resetPwaUpdateState() {
  manualUpdateFound.value = false
  updateAvailableVersion.value = null
  isCheckingForUpdates.value = false
  isDownloadingUpdate.value = false
  isReloading.value = false
  lastCheckTimestamp.value = null
  registeredSW.value = null
  if (rawNeedRefreshRef) {
    rawNeedRefreshRef.value = false
  }
  if (typeof window !== 'undefined' && window.sessionStorage) {
    sessionStorage.removeItem('pwa_just_updated_at')
  }
}

export function isVersionNewer(remoteVersion: string, localVersion: string): boolean {
  if (!remoteVersion || !localVersion) return false
  const p1 = remoteVersion.replace(/^v/, '').split('.').map(n => parseInt(n, 10) || 0)
  const p2 = localVersion.replace(/^v/, '').split('.').map(n => parseInt(n, 10) || 0)
  for (let i = 0; i < Math.max(p1.length, p2.length); i++) {
    const num1 = p1[i] || 0
    const num2 = p2[i] || 0
    if (num1 > num2) return true
    if (num1 < num2) return false
  }
  return false
}

export function isDevEnvironment(): boolean {
  if (typeof process !== 'undefined' && process.env.VITEST) {
    return false
  }
  if (typeof window === 'undefined') return false
  return Boolean(
    import.meta.env.DEV ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.port === '5173'
  )
}

export function isCrawlerOrBot(customUa?: string): boolean {
  if (customUa) {
    return /bot|googlebot|crawler|spider|robot|crawling|lighthouse|headless|wrs|pagespeed|pingdom|ptst|speed insights|insights/i.test(customUa)
  }
  if (typeof process !== 'undefined' && process.env.VITEST) {
    return false
  }
  if (typeof window === 'undefined') return false
  if (typeof navigator === 'undefined') return false
  if ((navigator as any).webdriver) return true
  if ((window as any).__PRERENDER_INJECTED || (window as any).__LIGHTHOUSE_TEST__) return true

  const ua = navigator.userAgent || ''
  return /bot|googlebot|crawler|spider|robot|crawling|lighthouse|headless|wrs|pagespeed|pingdom|ptst|speed insights|insights/i.test(ua)
}

export function usePwaUpdate() {
  const store = useAppStore()

  if (isCrawlerOrBot()) {
    if (!rawNeedRefreshRef) {
      rawNeedRefreshRef = ref(false)
      rawUpdateSWFn = async () => {}
    }
  } else if (!rawNeedRefreshRef) {
    try {
      const sw = useRegisterSW({
        immediate: true,
        onRegistered(registration) {
          if (registration) {
            registeredSW.value = registration
            // Listen for any background updates found by browser
            registration.addEventListener('updatefound', () => {
              const newWorker = registration.installing
              if (newWorker) {
                isDownloadingUpdate.value = true
                newWorker.addEventListener('statechange', () => {
                  if (newWorker.state === 'installed') {
                    isDownloadingUpdate.value = false
                    if (registration.waiting || navigator.serviceWorker?.controller) {
                      manualUpdateFound.value = true
                      if (rawNeedRefreshRef) rawNeedRefreshRef.value = true
                    }
                  } else if (newWorker.state === 'redundant') {
                    isDownloadingUpdate.value = false
                  }
                })
              }
            })
          }
        },
        onNeedRefresh() {
          if (!isDevEnvironment()) {
            manualUpdateFound.value = true
            if (rawNeedRefreshRef) rawNeedRefreshRef.value = true
          }
        },
        onRegisterError(err) {
          const msg = String(err?.message || err || '')
          if (msg.includes('Rejected') || isCrawlerOrBot()) {
            return
          }
          console.warn('[PWA] Service Worker registration failed:', err)
        }
      })
      rawNeedRefreshRef = sw.needRefresh
      rawUpdateSWFn = sw.updateServiceWorker
    } catch (e) {
      console.warn('[PWA] useRegisterSW could not be initialized:', e)
    }
  }

  const needRefresh = computed(() => {
    if (isDevEnvironment()) return false
    return Boolean(rawNeedRefreshRef?.value || manualUpdateFound.value)
  })

  const reloadApp = async () => {
    if (isReloading.value) return
    isReloading.value = true

    // Safety timeout: reset isReloading if reload takes longer than 4s or fails,
    // so clicks are never permanently locked out!
    setTimeout(() => {
      isReloading.value = false
    }, 4000)

    // Dismiss banner immediately so user doesn't see it during reload transition
    manualUpdateFound.value = false
    isDownloadingUpdate.value = false
    if (rawNeedRefreshRef) rawNeedRefreshRef.value = false
    updateAvailableVersion.value = null

    let reg = registeredSW.value
    if (!reg && typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      try {
        const fetchedReg = await navigator.serviceWorker.getRegistration()
        if (fetchedReg) registeredSW.value = fetchedReg
        reg = fetchedReg ?? null
      } catch {}
    }

    const triggerReload = () => {
      let reloaded = false
      const doReload = () => {
        if (reloaded) return
        reloaded = true
        window.location.reload()
      }
      if (typeof navigator !== 'undefined' && navigator.serviceWorker && typeof navigator.serviceWorker.addEventListener === 'function') {
        navigator.serviceWorker.addEventListener('controllerchange', doReload, { once: true })
      }
      setTimeout(doReload, 1000)
    }

    // 1. If SW is already waiting, trigger skip-waiting immediately
    if (reg?.waiting) {
      try {
        reg.waiting.postMessage({ type: 'SKIP_WAITING' })
      } catch {}
      if (rawUpdateSWFn) {
        try {
          await rawUpdateSWFn(true)
        } catch {}
      }
      triggerReload()
      return
    }

    // 2. If SW is currently downloading/installing, wait for it to finish and reach waiting!
    if (reg?.installing) {
      const worker = reg.installing
      await new Promise<void>((resolve) => {
        const timeout = setTimeout(resolve, 8000)
        worker.addEventListener('statechange', () => {
          if (worker.state === 'installed' || worker.state === 'redundant') {
            clearTimeout(timeout)
            resolve()
          }
        })
      })
      if (reg.waiting) {
        try {
          reg.waiting.postMessage({ type: 'SKIP_WAITING' })
        } catch {}
        if (rawUpdateSWFn) {
          try {
            await rawUpdateSWFn(true)
          } catch {}
        }
        triggerReload()
        return
      }
    }

    // 3. Fallback
    window.location.reload()
  }

  const checkForUpdates = async (isManual = false): Promise<{ hasUpdate: boolean; version?: string; error?: string }> => {
    if (typeof window === 'undefined') return { hasUpdate: false }

    const currentVersion = (typeof __APP_VERSION__ !== 'undefined' && __APP_VERSION__) ? __APP_VERSION__ : '0.0.44'

    if (isCrawlerOrBot()) {
      return { hasUpdate: false, version: currentVersion }
    }

    // Suppress automatic update popups in local development (http://localhost:5173/)
    if (isDevEnvironment()) {
      if (isManual) {
        store.showToast(`Entwicklungsmodus aktiv (v${currentVersion}) – keine Live-Updates.`, 'info')
      }
      return { hasUpdate: false, version: currentVersion }
    }

    if (!navigator.onLine) {
      if (isManual) {
        store.showToast('Offline: Keine Verbindung zum Update-Server.', 'error')
      }
      return { hasUpdate: false, error: 'offline' }
    }

    if (isCheckingForUpdates.value) {
      return { hasUpdate: needRefresh.value, version: updateAvailableVersion.value || undefined }
    }

    isCheckingForUpdates.value = true

    try {
      // 1. Check Service Worker registration update
      let reg: ServiceWorkerRegistration | undefined | null = registeredSW.value
      if (!reg && typeof navigator !== 'undefined' && navigator.serviceWorker && typeof navigator.serviceWorker.getRegistration === 'function') {
        reg = await navigator.serviceWorker.getRegistration()
        if (reg) registeredSW.value = reg
      }

      if (reg) {
        try {
          await reg.update()
        } catch (swErr) {
          console.warn('[PWA] reg.update error:', swErr)
        }

        if (reg.waiting) {
          manualUpdateFound.value = true
          isDownloadingUpdate.value = false
          if (rawNeedRefreshRef) rawNeedRefreshRef.value = true
        } else if (reg.installing) {
          isDownloadingUpdate.value = true
          const worker = reg.installing
          worker.addEventListener('statechange', () => {
            if (worker.state === 'installed') {
              isDownloadingUpdate.value = false
              if (reg?.waiting) {
                manualUpdateFound.value = true
                if (rawNeedRefreshRef) rawNeedRefreshRef.value = true
                if (isManual) {
                  store.showToast(
                    `Neues Update bereit! Jetzt aktualisieren.`,
                    'success',
                    {
                      label: 'Neu laden',
                      callback: reloadApp
                    }
                  )
                }
              }
            } else if (worker.state === 'redundant') {
              isDownloadingUpdate.value = false
            }
          })
        }
      }

      // 2. Fetch /version.json with no-store to immediately discover deployments
      let serverVersion: string | null = null
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' },
          priority: 'low' as any
        })
        if (res.ok) {
          const data = await res.json()
          if (data && data.version) {
            serverVersion = data.version
          }
        }
      } catch (fetchErr) {
        console.warn('[PWA] version.json fetch error:', fetchErr)
      }

      lastCheckTimestamp.value = Date.now()

      // If we got a valid serverVersion and it is NOT newer than currentVersion,
      // the running client is already on the newest build! Clear stale waiting state.
      if (serverVersion && !isVersionNewer(serverVersion, currentVersion)) {
        if (!reg?.waiting) {
          manualUpdateFound.value = false
          if (rawNeedRefreshRef) rawNeedRefreshRef.value = false
          updateAvailableVersion.value = null
        }
        if (isManual) {
          store.showToast(`Die App ist auf dem neuesten Stand (v${currentVersion}).`, 'info')
        }
        return { hasUpdate: false, version: currentVersion }
      }

      const isNewerVersion = Boolean(serverVersion && isVersionNewer(serverVersion, currentVersion))

      if (serverVersion) {
        updateAvailableVersion.value = serverVersion
      }

      // If SW is already waiting, signal update immediately!
      if (reg?.waiting || rawNeedRefreshRef?.value) {
        manualUpdateFound.value = true
        if (rawNeedRefreshRef) rawNeedRefreshRef.value = true

        if (isManual) {
          store.showToast(
            `Neues Update (${serverVersion ? 'v' + serverVersion : 'verfügbar'})! Jetzt aktualisieren.`,
            'success',
            {
              label: 'Neu laden',
              callback: reloadApp
            }
          )
        }
        return { hasUpdate: true, version: serverVersion || undefined }
      }

      // If newer version is on server:
      if (isNewerVersion) {
        if (reg?.installing) {
          isDownloadingUpdate.value = true
          if (isManual) {
            store.showToast(`Update v${serverVersion} wird im Hintergrund geladen...`, 'info')
          }
          return { hasUpdate: true, version: serverVersion || undefined }
        }

        if (reg) {
          // SW registration exists, so an update is being fetched in the background.
          // Do NOT show the reload banner immediately; wait for the SW to install!
          isDownloadingUpdate.value = true

          const trackWorker = (worker: ServiceWorker) => {
            worker.addEventListener('statechange', () => {
              if (worker.state === 'installed') {
                isDownloadingUpdate.value = false
                manualUpdateFound.value = true
                if (rawNeedRefreshRef) rawNeedRefreshRef.value = true
                if (isManual) {
                  store.showToast(
                    `Neues Update (${serverVersion ? 'v' + serverVersion : 'bereit'})! Jetzt aktualisieren.`,
                    'success',
                    {
                      label: 'Neu laden',
                      callback: reloadApp
                    }
                  )
                }
              } else if (worker.state === 'redundant') {
                isDownloadingUpdate.value = false
              }
            })
          }

          if (reg.installing) {
            trackWorker(reg.installing)
          } else {
            reg.addEventListener('updatefound', () => {
              if (reg.installing) {
                trackWorker(reg.installing)
              }
            }, { once: true })
          }

          // Fallback: If after 6s no worker finished installing, then allow manual reload
          setTimeout(() => {
            if (!reg.waiting && isDownloadingUpdate.value) {
              isDownloadingUpdate.value = false
              manualUpdateFound.value = true
              if (rawNeedRefreshRef) rawNeedRefreshRef.value = true
            }
          }, 6000)

          if (isManual) {
            store.showToast(`Update v${serverVersion} wird im Hintergrund geladen...`, 'info')
          }
          return { hasUpdate: true, version: serverVersion || undefined }
        }

        // Fallback if no SW or environment without SW
        manualUpdateFound.value = true
        if (rawNeedRefreshRef) rawNeedRefreshRef.value = true
        if (isManual) {
          store.showToast(
            `Neues Update (${serverVersion ? 'v' + serverVersion : 'verfügbar'})! Jetzt aktualisieren.`,
            'success',
            {
              label: 'Neu laden',
              callback: reloadApp
            }
          )
        }
        return { hasUpdate: true, version: serverVersion || undefined }
      }

      if (isManual) {
        store.showToast(`Die App ist auf dem neuesten Stand (v${currentVersion}).`, 'info')
      }

      return { hasUpdate: false, version: currentVersion }
    } catch (err: any) {
      console.error('[PWA] Update check failed:', err)
      if (isManual) {
        store.showToast('Fehler bei der Update-Prüfung.', 'error')
      }
      return { hasUpdate: false, error: err?.message || 'unknown' }
    } finally {
      isCheckingForUpdates.value = false
    }
  }

  // Setup background event listeners once
  if (!listenersInitialized && typeof window !== 'undefined') {
    listenersInitialized = true

    const isVitest = typeof process !== 'undefined' && Boolean(process.env.VITEST)
    if (!isDevEnvironment() && !isVitest && !isCrawlerOrBot()) {
      // Defer initial check to idle period (12 seconds after launch) so it never
      // interferes with initial rendering or appears in critical network chains (Lighthouse)
      const scheduleInitialCheck = () => {
        if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
          ;(window as any).requestIdleCallback(
            () => {
              checkForUpdates(false)
            },
            { timeout: 15000 }
          )
        } else {
          checkForUpdates(false)
        }
      }
      setTimeout(scheduleInitialCheck, 12000)

      // Periodic check every 15 minutes
      setInterval(() => {
        checkForUpdates(false)
      }, 15 * 60 * 1000)

      // Check when user resumes app or tab becomes visible
      if (typeof document !== 'undefined') {
        let lastCheck = 0
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            const now = Date.now()
            if (now - lastCheck > 60 * 1000) {
              lastCheck = now
              checkForUpdates(false)
            }
          }
        })
      }

      // Check when device comes back online
      window.addEventListener('online', () => {
        checkForUpdates(false)
      })
    }
  }

  return {
    needRefresh,
    isCheckingForUpdates,
    isDownloadingUpdate,
    lastCheckTimestamp,
    updateAvailableVersion,
    reloadApp,
    checkForUpdates,
    updateServiceWorker: rawUpdateSWFn || (async () => { window.location.reload() })
  }
}
